import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Trash2, Check, Ship, Calendar as CalendarIcon, DollarSign, ShoppingBag, TrendingDown, Receipt, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

// Convert ISO yyyy-MM-dd to Date (local) and back, without UTC drift
const parseISODate = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const formatISODate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const STORAGE_LABELS = { fridge: "Kjølevare", freezer: "Frysevare", dry: "Tørrproviant" };

export default function TripsPage() {
  const { loadActiveBoat } = useAuth();
  const [trips, setTrips] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", budget: "", shift: "1", trip_number: "1" });
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [budget, setBudget] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyTrip, setHistoryTrip] = useState(null);

  const loadTrips = useCallback(async () => {
    try {
      const res = await api.getTrips();
      setTrips(res.data);
      const active = res.data.find((t) => t.is_active);
      if (active && !selectedTrip) setSelectedTrip(active.id);
    } catch { toast.error("Kunne ikke laste turer"); }
  }, [selectedTrip]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const loadBudget = useCallback(async () => {
    if (!selectedTrip) { setBudget(null); return; }
    try {
      const res = await api.getTripBudget(selectedTrip);
      setBudget(res.data);
    } catch { setBudget(null); }
  }, [selectedTrip]);

  useEffect(() => { loadBudget(); }, [loadBudget]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.start_date) { toast.error("Navn og startdato er påkrevd"); return; }
    try {
      await api.createTrip({ ...form, budget: parseFloat(form.budget) || 0, shift: form.shift, trip_number: parseInt(form.trip_number) || 1 });
      toast.success(`Tur ${form.trip_number} opprettet og satt som aktiv for Skift ${form.shift}`);
      setFormOpen(false);
      setForm({ name: "", start_date: "", end_date: "", budget: "", shift: "1", trip_number: "1" });
      loadTrips();
      // Refresh active boat so the Turnusplan / header pill / shift-info
      // widget pick up the new shift_config without needing a hard reload.
      if (loadActiveBoat) await loadActiveBoat();
    } catch { toast.error("Feil ved oppretting"); }
  };

  const handleActivate = async (id) => {
    try {
      await api.activateTrip(id);
      setSelectedTrip(id);
      toast.success("Tur aktivert");
      loadTrips();
    } catch { toast.error("Feil"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteTrip(id);
      if (selectedTrip === id) setSelectedTrip(null);
      toast.success("Tur slettet");
      loadTrips();
    } catch { toast.error("Feil ved sletting"); }
  };

  // Inline-rediger skift / tur-nummer på ein eksisterande tur
  const handleUpdateTripField = async (tripId, field, value) => {
    try {
      const payload = field === "trip_number" ? { trip_number: parseInt(value) || 1 } : { [field]: value };
      await api.updateTrip(tripId, payload);
      const msg =
        field === "shift" ? `Tur flytta til Skift ${value}` :
        field === "start_date" ? "Startdato oppdatert" :
        field === "end_date" ? "Sluttdato oppdatert" :
        "Tur oppdatert";
      toast.success(msg);
      loadTrips();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oppdatere tur");
    }
  };

  const viewHistory = async (trip) => {
    setHistoryTrip(trip);
    try {
      const res = await api.getTripHistory(trip.id);
      setHistory(res.data);
    } catch { toast.error("Kunne ikke laste historikk"); }
  };

  const handleExportPdf = async (tripId, tripName) => {
    try {
      toast.loading("Genererer PDF...", { id: "pdf-export" });
      const res = await api.exportTripBudgetPdf(tripId);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const safe = (tripName || "tur").replace(/[^a-zA-Z0-9-_]/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `turbudsjett-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF eksportert", { id: "pdf-export" });
    } catch {
      toast.error("Kunne ikke generere PDF", { id: "pdf-export" });
    }
  };

  const spentPercent = budget ? Math.min(100, Math.round((budget.total_spent / Math.max(budget.trip.budget, 1)) * 100)) : 0;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="trips-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Turer</h1>
          <p className="text-sm text-stone-500 mt-1">Administrer turer og matbudsjett</p>
        </div>
        <button onClick={() => setFormOpen(true)} className="inline-flex items-center gap-2 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="create-trip-btn">
          <Plus className="h-4 w-4" /> Ny tur
        </button>
      </div>

      {/* Trip list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {trips.map((trip) => (
          <div key={trip.id} className={`bg-white border rounded-xl p-4 transition-all ${trip.is_active ? "border-seawater-500 ring-1 ring-navy-100" : "border-stone-100"} ${selectedTrip === trip.id ? "shadow-md" : ""}`} data-testid={`trip-card-${trip.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ship className="h-5 w-5 text-navy-800" />
                <h3 className="text-base font-semibold text-stone-800 cursor-pointer hover:text-navy-800 transition-colors" onClick={() => setSelectedTrip(trip.id)} data-testid={`trip-name-${trip.id}`}>{trip.name}</h3>
              </div>
              <div className="flex items-center gap-1">
                {!trip.is_active && (
                  <button onClick={() => handleActivate(trip.id)} className="p-1.5 rounded-md hover:bg-sailcloth-100 text-stone-400 hover:text-seawater-600 transition-colors" title="Aktiver tur" data-testid={`activate-trip-${trip.id}`}>
                    <Check className="h-4 w-4" />
                  </button>
                )}
                <button onClick={() => handleDelete(trip.id)} className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-trip-${trip.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-stone-500 mb-2 flex-wrap">
              <span className="flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" />
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="px-1.5 py-0.5 rounded hover:bg-stone-100 text-stone-600 font-medium transition-colors"
                      title="Endre startdato"
                      data-testid={`trip-start-date-btn-${trip.id}`}
                    >
                      {trip.start_date || "Velg start"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={parseISODate(trip.start_date)}
                      defaultMonth={parseISODate(trip.start_date)}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = formatISODate(d);
                        if (iso !== trip.start_date) handleUpdateTripField(trip.id, "start_date", iso);
                      }}
                      weekStartsOn={1}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-stone-300">–</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="px-1.5 py-0.5 rounded hover:bg-stone-100 text-stone-600 font-medium transition-colors"
                      title="Endre sluttdato"
                      data-testid={`trip-end-date-btn-${trip.id}`}
                    >
                      {trip.end_date || "Velg slutt"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={parseISODate(trip.end_date)}
                      defaultMonth={parseISODate(trip.end_date) || parseISODate(trip.start_date)}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = formatISODate(d);
                        if (iso !== trip.end_date) handleUpdateTripField(trip.id, "end_date", iso);
                      }}
                      weekStartsOn={1}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </span>
              <label className="inline-flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded text-stone-600 font-medium" title="Endre skift for denne turen">
                Skift
                <select
                  value={trip.shift || "1"}
                  onChange={(e) => handleUpdateTripField(trip.id, "shift", e.target.value)}
                  className="bg-transparent border-0 outline-none font-semibold text-stone-700 cursor-pointer pr-0.5"
                  data-testid={`trip-shift-select-${trip.id}`}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded text-stone-600 font-medium" title="Endre tur-nummer">
                Tur
                <input
                  type="number"
                  min="1"
                  value={trip.trip_number || 1}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTrips((prev) => prev.map((t) => t.id === trip.id ? { ...t, trip_number: v } : t));
                  }}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value) || 1;
                    if (v !== (trip.trip_number || 1)) handleUpdateTripField(trip.id, "trip_number", v);
                  }}
                  className="bg-transparent border-0 outline-none font-semibold text-stone-700 w-10 text-center"
                  data-testid={`trip-number-input-${trip.id}`}
                />
              </label>
            </div>
            <div className="flex items-center gap-2">
              {trip.is_active && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-navy-800 bg-sailcloth-100 px-2 py-0.5 rounded-full">Aktiv tur</span>}
              {!trip.is_active && (
                <button onClick={() => viewHistory(trip)} className="text-[10px] font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors" data-testid={`view-history-${trip.id}`}>
                  Se historikk
                </button>
              )}
            </div>
            {trip.budget > 0 && <p className="text-xs text-stone-400 mt-1">Budsjett: {trip.budget.toLocaleString("nb-NO")} kr</p>}
          </div>
        ))}
        {trips.length === 0 && (
          <div className="col-span-full text-center py-16">
            <Ship className="h-12 w-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen turer ennå</h3>
            <p className="text-sm text-stone-400 mt-1">Opprett en tur for å begynne å spore matbudsjettet</p>
          </div>
        )}
      </div>

      {/* Budget Dashboard */}
      {budget && (
        <div className="space-y-6" data-testid="budget-dashboard">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-2xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Budsjett: {budget.trip.name}
            </h2>
            <button
              onClick={() => handleExportPdf(budget.trip.id, budget.trip.name)}
              className="inline-flex items-center gap-2 rounded-md border border-navy-800 bg-white px-3 py-2 text-sm font-medium text-navy-800 hover:bg-sailcloth-100 transition-colors"
              data-testid="export-budget-pdf-btn"
            >
              <FileDown className="h-4 w-4" /> Eksporter PDF
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><DollarSign className="h-4 w-4 text-seawater-600" /><span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Budsjett</span></div>
              <span className="text-2xl font-bold text-stone-800" data-testid="budget-total">{budget.trip.budget.toLocaleString("nb-NO")} kr</span>
            </div>
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><ShoppingBag className="h-4 w-4 text-orange-600" /><span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Brukt</span></div>
              <span className="text-2xl font-bold text-stone-800" data-testid="budget-spent">{budget.total_spent.toLocaleString("nb-NO")} kr</span>
            </div>
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><TrendingDown className={`h-4 w-4 ${budget.remaining >= 0 ? "text-seawater-600" : "text-red-600"}`} /><span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Gjenstår</span></div>
              <span className={`text-2xl font-bold ${budget.remaining >= 0 ? "text-navy-800" : "text-red-600"}`} data-testid="budget-remaining">{budget.remaining.toLocaleString("nb-NO")} kr</span>
            </div>
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Receipt className="h-4 w-4 text-blue-600" /><span className="text-xs font-medium text-stone-500 uppercase tracking-wider">Kvitteringer</span></div>
              <span className="text-2xl font-bold text-stone-800" data-testid="receipt-count">{budget.receipt_count}</span>
            </div>
          </div>

          {/* Progress bar */}
          {budget.trip.budget > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-stone-600">Forbruk</span>
                <span className="text-sm font-semibold text-stone-800">{spentPercent}%</span>
              </div>
              <div className="w-full bg-stone-100 rounded-full h-3">
                <div className={`h-3 rounded-full transition-all ${spentPercent > 90 ? "bg-red-500" : spentPercent > 70 ? "bg-amber-500" : "bg-seawater-500"}`} style={{ width: `${spentPercent}%` }} data-testid="budget-progress-bar" />
              </div>
            </div>
          )}

          {/* By store */}
          {Object.keys(budget.by_store).length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Per butikk</h3>
              <div className="space-y-2">
                {Object.entries(budget.by_store).sort((a, b) => b[1] - a[1]).map(([store, amount]) => (
                  <div key={store} className="flex items-center justify-between">
                    <span className="text-sm text-stone-600">{store}</span>
                    <span className="text-sm font-semibold text-stone-800">{amount.toLocaleString("nb-NO")} kr</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per lagringstype */}
          {Object.keys(budget.by_category).length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Per lagringstype</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(budget.by_category).map(([cat, amount]) => (
                  <div key={cat} className={`rounded-lg p-3 text-center ${cat === "fridge" ? "bg-blue-50 text-blue-700" : cat === "freezer" ? "bg-cyan-50 text-cyan-700" : "bg-amber-50 text-amber-700"}`}>
                    <span className="text-lg font-bold block">{amount.toLocaleString("nb-NO")} kr</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold">{STORAGE_LABELS[cat] || cat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Statistikk for pant, rabatt, bæreposer, handlenett — per tur */}
          {budget.extras_summary && Object.keys(budget.extras_summary).length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="trip-extras-card">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Pant, poser & rabatt</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(() => {
                  const meta = {
                    pant: { label: "Pant", bg: "bg-emerald-50", color: "text-emerald-700" },
                    rabatt: { label: "Rabatt", bg: "bg-rose-50", color: "text-rose-700" },
                    baerepose: { label: "Bæreposer", bg: "bg-orange-50", color: "text-orange-700" },
                    handlenett: { label: "Handlenett", bg: "bg-amber-50", color: "text-amber-700" },
                    annet: { label: "Annet", bg: "bg-stone-50", color: "text-stone-700" },
                  };
                  const order = ["pant", "baerepose", "handlenett", "rabatt", "annet"];
                  const entries = Object.entries(budget.extras_summary)
                    .sort((a, b) => (order.indexOf(a[0]) - order.indexOf(b[0])));
                  return entries.map(([kind, stats]) => {
                    const m = meta[kind] || { label: kind, bg: "bg-stone-50", color: "text-stone-700" };
                    const price = Number(stats.total_price || 0);
                    const sign = kind === "rabatt" ? "-" : "";
                    return (
                      <div key={kind} className={`rounded-lg p-3 ${m.bg}`} data-testid={`trip-extras-${kind}`}>
                        <div className={`text-[10px] uppercase tracking-wider font-semibold ${m.color}`}>{m.label}</div>
                        <div className={`text-lg font-bold ${m.color}`} data-testid={`trip-extras-${kind}-count`}>{stats.count} stk</div>
                        {price !== 0 && (
                          <div className={`text-xs font-semibold ${m.color}`} data-testid={`trip-extras-${kind}-price`}>
                            {sign}{Math.abs(price).toLocaleString("nb-NO")} kr
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Receipt list */}
          {budget.receipts.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Kvitteringer</h3>
              <div className="space-y-2">
                {budget.receipts.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0" data-testid={`receipt-row-${r.id}`}>
                    <div>
                      <span className="text-sm font-medium text-stone-700">{r.store_name || "Ukjent butikk"}</span>
                      <span className="text-xs text-stone-400 ml-2">{r.date}</span>
                      <span className="text-xs text-stone-400 ml-2">{r.items?.length || 0} varer</span>
                    </div>
                    <span className="text-sm font-semibold text-stone-800">{(r.total_amount || 0).toLocaleString("nb-NO")} kr</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create trip dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="create-trip-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ny tur</DialogTitle>
            <DialogDescription>Opprett en ny tur for å spore matbudsjett</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Turnavn (f.eks. Tur 3 - Mars 2026)" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="trip-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Skift</label>
                <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="trip-shift-select">
                  <option value="1">Skift 1</option>
                  <option value="2">Skift 2</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Tur nummer</label>
                <input type="number" min="1" value={form.trip_number} onChange={(e) => setForm({ ...form, trip_number: e.target.value })} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="trip-number-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Startdato</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="trip-start-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Sluttdato</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="trip-end-date" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Budsjett (kr)</label>
              <input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="0" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="trip-budget-input" />
            </div>
            <button onClick={handleCreate} className="w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="save-trip-btn">
              Opprett tur
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trip history dialog */}
      <Dialog open={!!historyTrip} onOpenChange={(o) => { if (!o) { setHistoryTrip(null); setHistory(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="trip-history-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Historikk: {historyTrip?.name}
              {historyTrip?.shift && <span className="text-sm font-normal text-stone-400 ml-2">Skift {historyTrip.shift}, Tur {historyTrip.trip_number || "?"}</span>}
            </DialogTitle>
            <DialogDescription>{historyTrip?.start_date}{historyTrip?.end_date ? ` – ${historyTrip.end_date}` : ""}</DialogDescription>
          </DialogHeader>
          {history ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <span className="text-lg font-bold text-stone-800">{history.meal_plans?.length || 0}</span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-500 block">Ukeplaner</span>
                </div>
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <span className="text-lg font-bold text-stone-800">{history.receipts?.length || 0}</span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-500 block">Kvitteringer</span>
                </div>
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <span className="text-lg font-bold text-navy-800">{(history.total_spent || 0).toLocaleString("nb-NO")} kr</span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-500 block">Totalt brukt</span>
                </div>
              </div>

              {/* Pantry snapshot */}
              {history.pantry_snapshot?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-2">Beholdning ved turens slutt</h3>
                  <div className="bg-white border border-stone-100 rounded-lg divide-y divide-stone-50 max-h-48 overflow-y-auto">
                    {history.pantry_snapshot.map((item, i) => (
                      <div key={`pantry-${i}-${item.name || ""}`} className="flex items-center justify-between px-3 py-2">
                        <span className="text-sm text-stone-700">{item.name}</span>
                        <span className="text-xs font-medium bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{item.amount} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shopping lists */}
              {history.shopping_lists?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-2">Handlelister</h3>
                  {history.shopping_lists.map((sl, i) => (
                    <div key={`sl-${sl.week_start || i}`} className="bg-white border border-stone-100 rounded-lg p-3 mb-2">
                      <span className="text-xs font-medium text-stone-500">Uke {sl.week_start}</span>
                      <div className="mt-1 space-y-0.5">
                        {(sl.items || []).map((item, j) => (
                          <div key={`sli-${j}-${item.name || ""}`} className="flex items-center gap-2 text-xs text-stone-600">
                            <span className={`w-2 h-2 rounded-full ${item.status === "kjøpt" ? "bg-seawater-500" : item.status === "har_om_bord" ? "bg-blue-500" : "bg-stone-300"}`} />
                            <span>{item.name}</span>
                            {item.amount && <span className="text-stone-400">{item.amount} {item.unit}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Receipts */}
              {history.receipts?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-2">Kvitteringer</h3>
                  <div className="space-y-1">
                    {history.receipts.map((r) => (
                      <div key={r.id} className="flex items-center justify-between py-1.5 px-3 bg-stone-50 rounded-lg">
                        <div>
                          <span className="text-sm text-stone-700">{r.store_name || "Ukjent"}</span>
                          <span className="text-xs text-stone-400 ml-2">{r.date}</span>
                        </div>
                        <span className="text-sm font-semibold text-stone-800">{(r.total_amount || 0).toLocaleString("nb-NO")} kr</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-stone-400">Laster historikk...</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
